import * as _ from 'underscore'

import { OpDepositoryElement, OpArticle } from "../../../../models/core"
import { Enums } from "../../../../models/common"
import { IJsonSerializable } from '../../../../models/util/interfaces'

import { TypeHelper } from '../../../../helpers'

export type AddOrderOptions = {
    content?: OpDepositoryElement[]
    readonly?: boolean

    onInitialized: (imrDataTypePanel: IAddOrder) => void
}

export interface IAddOrder {
    getArticles(): OpDepositoryElement[]
    refresh(): void
}

export class GoAddOrderContentItem implements IJsonSerializable {

    //#region Members

    public amount: number
    public article: OpArticle
    public depositoryElements: OpDepositoryElement[]
    public idArticle: number
    public idPackage: number
    public notes: string
    public previousOpSate: Enums.OpChangeState

    private _opState: Enums.OpChangeState
    public set opState(value: Enums.OpChangeState) {
        this.previousOpSate = this._opState
        this._opState = value
    }
    public get opState(): Enums.OpChangeState {
        return this._opState
    }

    //#endregion


    //#region Ctor

    constructor(obj: any = null) {
        if (obj != null) {
            this.fromJSON(obj)
        } else {
            this.idPackage = 0
            this.idArticle = 0
            this.opState = Enums.OpChangeState.New
        }
    }

    //#endregion


    //#region Article

    public updateArticle(articles: OpArticle[]): void {
        this.article = _.find(articles, (article: OpArticle) => article.idArticle === this.idArticle)
    }

    //#endregion


    //#region Copy

    public copy(orderItem: GoAddOrderContentItem): void {
        if (orderItem != null) {
            this.amount = orderItem.amount
            this.article = orderItem.article
            this.depositoryElements = TypeHelper.clone(orderItem.depositoryElements)
            this.idArticle = orderItem.idArticle
            this.idPackage = orderItem.idPackage
            this.notes = orderItem.notes
            this.opState = orderItem.opState
        }
    }

    //#endregion


    //#region Form Data

    public static fromFormData(formData: any, articles: OpArticle[]): GoAddOrderContentItem {
        const item: GoAddOrderContentItem = new GoAddOrderContentItem()

        item.copyFormData(formData, articles)

        return item
    }

    public copyFormData(formData: any, articles: OpArticle[]): void {
        if (formData != null) {
            this.article = _.find(articles, (article: OpArticle) => article.idArticle === formData.idArticle)
            this.amount = formData.amount
            this.idArticle = formData.idArticle
            this.notes = formData.notes
        }
    }

    public toFormData(): any {
        return {
            amount: this.amount,
            idArticle: this.idArticle,
            notes: this.notes,
        }
    }

    //#endregion


    //#region Content List

    public static fromContentList(contentList: OpDepositoryElement[], articles: OpArticle[] = []): GoAddOrderContentItem[] {
        const groupedContent: _.Dictionary<OpDepositoryElement[]> = _.groupBy(contentList, (contentItem: OpDepositoryElement) => {
            return contentItem.idArticle
        })

        return _.map(groupedContent, (elements: OpDepositoryElement[], key: string) => {
            const item: GoAddOrderContentItem = new GoAddOrderContentItem()
            const firstElement: OpDepositoryElement = _.first(elements)

            item.amount = _.size(elements)
            item.article = _.find(articles, (article: OpArticle) => article.idArticle === _.first(elements).idArticle)
            item.depositoryElements = elements
            item.idArticle = Number(key)
            item.opState = Enums.OpChangeState.Loaded

            if (firstElement != null) {
                item.notes = firstElement.notes
                item.idPackage = firstElement.idPackage
            }

            return item
        })
    }

    public toContentList(): OpDepositoryElement[] {
        switch (this.opState) {
            case Enums.OpChangeState.New:
                return this.createNewDepisitoryElements(this.amount)

            case Enums.OpChangeState.Delete:
                return this.createDeletedDepositoryElements()

            case Enums.OpChangeState.Modified:
                return this.createModifiedDepositoryElements()
        }

        return this.depositoryElements
    }

    private createDeletedDepositoryElements(): OpDepositoryElement[] {
        return _.map(this.depositoryElements, (depositoryElement: OpDepositoryElement) => {
            const clonedDepositoryElement = depositoryElement.clone()

            clonedDepositoryElement.opState = Enums.OpChangeState.Delete

            return clonedDepositoryElement
        })
    }

    private createModifiedDepositoryElements(): OpDepositoryElement[] {
        const depositoryElements: OpDepositoryElement[] = []

        if (this.amount === _.size(this.depositoryElements)) {
            _.forEach(this.depositoryElements, (depisotoryElement: OpDepositoryElement) => {
                const clonedDepositoryElement = depisotoryElement.clone()

                clonedDepositoryElement.opState = Enums.OpChangeState.Modified
                clonedDepositoryElement.notes = this.notes

                depositoryElements.push(clonedDepositoryElement)
            })
        } else {
            const deletedDepositoryElements: OpDepositoryElement[] = this.createDeletedDepositoryElements()
            const newDepisitoryElements: OpDepositoryElement[] = this.createNewDepisitoryElements(this.amount)

            depositoryElements.push(...deletedDepositoryElements, ...newDepisitoryElements)
        }

        return depositoryElements
    }

    private createNewDepisitoryElements(amount: number): OpDepositoryElement[] {
        const depositoryElements: OpDepositoryElement[] = []
        const date: Date = new Date()

        _.times(amount, () => {
            const depositoryElement: OpDepositoryElement = new OpDepositoryElement(null)

            depositoryElement.idArticle = this.idArticle
            depositoryElement.idDeviceStateType = 2
            depositoryElement.idDeviceTypeClass = 2
            depositoryElement.idPackage = this.idPackage
            depositoryElement.notes = this.notes
            depositoryElement.opState = Enums.OpChangeState.New
            depositoryElement.startDate = date

            depositoryElements.push(depositoryElement)
        })

        return depositoryElements
    }

    //#endregion    


    //#region IJsonSerializable Implementation

    public toJSON(compressed: boolean) {
        return {
            amount: this.amount,
            article: this.article,
            depositoryElements: this.depositoryElements != null ? _.map(this.depositoryElements, elem => elem.toJSON()) : null,
            idArticle: this.idArticle,
            idPackage: this.idPackage,
            notes: this.notes,
            opState: this.opState,
        }
    }

    public fromJSON(opObj: any, compressed: boolean = false) {
        if (opObj != null) {
            this.amount = opObj.amount
            this.article = opObj.article
            this.depositoryElements = _.map(opObj.depositoryElements, elem => new OpDepositoryElement(elem))
            this.idArticle = opObj.idArticle
            this.idPackage = opObj.idPackage
            this.notes = opObj.notes
            this.opState = opObj.opState
        }
    }

    //#endregion

}
